import React, { ChangeEvent, useCallback, useEffect, useState } from 'react';
import Grid from '@material-ui/core/Grid';
import { TextField } from '@material-ui/core';
import Infinity from '../../../../../assets/icons/infinity.svg';
import InfinityHighContrast from '../../../../../assets/icons/infinity-high-contrast.svg';
import { TripleSwitch } from '../../../../../components/TripleSwitch/TripleSwitch.component';
import { useStyles } from './SessionsParametersLimitCell.styles';
import TooltipOne from '../../../../../components/TooltipOne/TooltipOne.component';
import { getTooltipTitle, getTooltipTitleBlockingLimit } from '../helpers'
import SwitchOne from '../../../../../components/SwitchOne/SwitchOne.component';
import { highContrastMode } from '../../../../../utils/highContrastChecker';

interface SessionsParametersLimitCellProps<T> {
  type: keyof T;
  value: T;
  initialValue: number | null;
  idKey: keyof T;
  handleValueChange: (type: keyof T, value: number | null, id: string | number) => void;
  handleSwitchChange?: (type: keyof T, value: boolean, id: string | number) => void;
  validation?: (type: keyof T, value: number | null, limits: T) => boolean;
  isValid?: boolean;
  tripleSwitchMode?: boolean;
  isNotVisible?: boolean;
  noSwitchMode?: boolean;
  isSwitchDisabled?: boolean;
  ariaLabel?: string;
  examTaskGroupName?: string;
  isCardLimit?: boolean;
}

enum LimitValues {
  on = -1,
  off = 0,
  infinity = 1,
}

export const SessionsParametersLimitCell = <T,>({
  type,
  value,
  initialValue,
  handleValueChange,
  handleSwitchChange,
  validation,
  isValid,
  tripleSwitchMode,
  idKey,
  isNotVisible,
  noSwitchMode,
  isSwitchDisabled,
  ariaLabel,
  examTaskGroupName,
  isCardLimit
}: SessionsParametersLimitCellProps<T>) => {
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const [isInputDisabled, setIsInputDisabled] = useState<boolean>(false);
  const styles = useStyles();

  const id = `${value[idKey]}`;

  useEffect(() => {
    setIsChecked(initialValue !== null && initialValue > 0);
  }, [initialValue]);

  const onValueChange = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      const val = parseInt(e.target.value, 10);
      if (isSwitchDisabled && val === 0) return;
      if (validation) {
        validation(type, val, value);
      }
      handleValueChange(type, Number.isNaN(val) ? 0 : val, id);
    },
    [type, value, handleValueChange, validation, id],
  );

  const onSwitchChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      const defaultValue = typeof type === 'string' && type.includes('sequential') ? 2 : 1;
      if (handleSwitchChange) {
        handleSwitchChange(type, event.target.checked, id);
      }
      if (validation) {
        validation(type, checked ? defaultValue : 0, value);
      }
    },
    [handleSwitchChange, type, id, validation],
  );

  const onTripleSwitchChange = useCallback(
    (switchValue: number) => {
      if (switchValue === LimitValues.off) {
        handleValueChange(type, 0, id);
        if (validation) {
          validation(type, 0, value);
        }
      } else if (switchValue === LimitValues.on) {
        if (handleSwitchChange) {
          handleSwitchChange(type, true, id);
        }
        if (validation) {
          validation(type, 1, value);
        }
      } else {
        handleValueChange(type, null, id);
        if (validation) {
          validation(type, null, value);
        }
      }
      setIsInputDisabled(switchValue === LimitValues.infinity);
    },
    [handleSwitchChange, type, id, validation],
  );
  let tripleSwitchValue = LimitValues.off;
  if (initialValue === null) {
    tripleSwitchValue = LimitValues.infinity;
  } else if (initialValue > 0) {
    tripleSwitchValue = LimitValues.on;
  }

  const onSetInfinity = () => {
    onTripleSwitchChange(LimitValues.infinity);
    setIsInputDisabled(true);
  };

  return !isNotVisible ? (
    <Grid container wrap="nowrap" style={{ height: 50, alignItems: 'center' }}>
      <TextField
        variant="outlined"
        type="number"
        name={type.toString()}
        onChange={onValueChange}
        inputProps={{
          'aria-label': ariaLabel,
          style: {
            padding: '7px',
            width: '70px',
            fontSize: '1rem',
            height: 20,
          },
          min: isSwitchDisabled ? 1 : 0,
        }}
        value={initialValue || 0}
        error={isValid}
        disabled={isInputDisabled}
      />
      {!noSwitchMode && (
        <div className={styles.switch}>
          {tripleSwitchMode ? (
            <TripleSwitch
              value={tripleSwitchValue}
              possibleValues={[LimitValues.on, LimitValues.off, LimitValues.infinity]}
              greyedValues={[LimitValues.off]}
              onChange={onTripleSwitchChange}
              examTaskGroupName={examTaskGroupName}
            />
          ) : (
            <TooltipOne
              title={
                isCardLimit
                  ? getTooltipTitle(type as string, isChecked)
                  : getTooltipTitleBlockingLimit(type as string, isChecked, examTaskGroupName)
              }
              placement="top"
            >
              <span>
                <SwitchOne
                  disableRipple
                  disabled={isSwitchDisabled}
                  inputProps={{
                    'aria-label': 'limit',
                  }}
                  checked={isChecked}
                  color={'primary'}
                  onChange={onSwitchChange}
                />
              </span>
            </TooltipOne>
          )}
        </div>
      )}
      {tripleSwitchMode && (
        <TooltipOne title="Nieograniczony" placement="top">
          <div className={styles.infinity}>
            <img
              src={highContrastMode ? InfinityHighContrast : Infinity}
              onClick={onSetInfinity}
              alt="ikona potrójnego przełącznika"
            />
          </div>
        </TooltipOne>
      )}
    </Grid>
  ) : null;
};